import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, Type, Contrast, BookOpen, Ear, Link2, Keyboard, Sparkles,
  ChevronDown, CheckCircle, ArrowRight, Zap, Gauge, Layers, Eye,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const BOX_IMG = `${BACKEND_URL}/api/builder/media/files/products/ai-joomla-accessibility-widget/images/ai_joomla_accessibility_box.png`;
const BUY_URL = '/extensions/plugins/ai-joomla-accessibility-widget';
const HERO_BG = 'https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const CAPABILITIES = [
  { icon: Type, title: 'Text Size & Spacing', desc: 'Visitors scale font size, line height, and letter spacing on the fly — no layout breaking.', span: 'md:col-span-5' },
  { icon: Contrast, title: 'Contrast & Color Modes', desc: 'Dark contrast, light contrast, monochrome, and saturation adjustments for low-vision visitors.', span: 'md:col-span-4' },
  { icon: BookOpen, title: 'Dyslexia-Friendly Font', desc: 'One click swaps body text to a dyslexia-friendly typeface, site-wide.', span: 'md:col-span-3' },
  { icon: Ear, title: 'Screen Reader / TTS', desc: 'Built-in text-to-speech reads page content aloud for visually impaired visitors.', span: 'md:col-span-4' },
  { icon: Eye, title: 'Link & Heading Highlighting', desc: 'Underline every link or outline every heading so page structure is instantly scannable.', span: 'md:col-span-4' },
  { icon: Keyboard, title: 'Keyboard Navigation', desc: 'Full keyboard-only support with a visible focus outline for motor-impaired visitors.', span: 'md:col-span-4' },
  { icon: Link2, title: 'Custom Quick Links', desc: 'Pin your own accessibility statement, contact page, or sitemap straight into the widget panel.', span: 'md:col-span-12' },
];

const STATS = [
  { icon: ShieldCheck, n: 'WCAG 2.1', l: 'Adjustment Toolkit' },
  { icon: Ear, n: 'Screen-Reader', l: 'Ready Out of the Box' },
  { icon: Gauge, n: 'Zero', l: 'Layout Shift' },
  { icon: Layers, n: '1-Click', l: 'Install & Update' },
];

const FAQS = [
  { q: 'Does this make my site 100% ADA compliant?', a: 'No single tool can guarantee full legal compliance on its own — accessibility depends on your content and markup too. The widget gives visitors the adjustment tools WCAG 2.1 calls for (resize, contrast, dyslexia font, screen reader, highlighting, keyboard nav) as a strong, visible step toward compliance.' },
  { q: 'Will it slow my site down?', a: 'No. The widget only loads its assets on pages where the module is published, and the panel renders on-demand when a visitor opens it — there\u2019s no layout shift on page load.' },
  { q: 'Does it work with my existing template?', a: 'Yes — it installs as a system plugin + module combo and overlays a floating button on top of any Joomla template, without touching your existing markup.' },
  { q: 'Is it compatible with Joomla 4, 5 and 6?', a: 'Yes — AI Joomla Accessibility Widget is built and tested for Joomla 4, 5 and 6.' },
  { q: 'What happens if my license lapses?', a: 'The widget keeps running exactly as installed. You\u2019ll just stop receiving new version updates and support until you renew.' },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left py-5 flex justify-between items-center gap-4 group focus:outline-none focus:ring-2 focus:ring-cyan-400/60 rounded"
        data-testid={`accessibility-faq-toggle-${q.slice(0, 12).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-white font-medium group-hover:text-cyan-400 transition-colors duration-200">{q}</span>
        <ChevronDown aria-hidden="true" className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="pb-5 text-slate-400 leading-relaxed text-sm">{a}</p>}
    </div>
  );
}

export default function AiJoomlaAccessibilityWidgetPage() {
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/store/products`).then((res) => {
      const items = Array.isArray(res.data) ? res.data : (res.data.products || []);
      const p = items.find((x) => x.extension_element === 'pkg_aijoomlaaccessibility');
      if (p?.license_options) setTiers(p.license_options.filter((o) => o.visible !== false));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050f17] text-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#050f17]" />
        <img src={HERO_BG} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_10%,_rgba(34,211,238,0.16),_transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/25 text-cyan-300 text-xs font-bold uppercase tracking-[0.2em] mb-6" data-testid="accessibility-hero-badge">
              <ShieldCheck aria-hidden="true" className="w-3.5 h-3.5" /> ADA &amp; WCAG Accessibility Toolkit
            </div>
            <h1 className="text-5xl sm:text-6xl tracking-tight leading-tight font-bold mb-6" data-testid="accessibility-hero-title">
              AI-Powered<br />
              <span className="bg-gradient-to-r from-cyan-300 to-cyan-500 bg-clip-text text-transparent">Accessibility for Joomla</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-8 max-w-xl">
              A floating accessibility toolbar that gives every visitor the WCAG 2.1 adjustment tools they need —
              text resizing, contrast modes, a dyslexia-friendly font, screen reader support, and more. Installs
              in minutes, no coding required.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to={BUY_URL}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#22d3ee] hover:bg-[#06b6d4] text-[#050f17] font-bold transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-[#050f17]"
                data-testid="accessibility-cta-buy-now"
              >
                <Zap aria-hidden="true" className="w-4 h-4" /> View Pricing &amp; Buy
              </Link>
              <Link
                to="/docs/ai-joomla-accessibility-widget"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/20 hover:border-white/40 text-white font-medium transition-colors duration-200"
                data-testid="accessibility-cta-docs"
              >
                <ArrowRight aria-hidden="true" className="w-4 h-4" /> View Documentation
              </Link>
            </div>
            <div className="flex flex-wrap gap-4 mt-8 text-xs text-slate-400">
              {['Joomla 4, 5 & 6', 'No coding required', 'Free tier available', '1-click install'].map((b) => (
                <span key={b} className="flex items-center gap-1"><CheckCircle aria-hidden="true" className="w-3 h-3 text-emerald-400" />{b}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-6 -right-6 bg-[#0f1f33] border border-cyan-400/30 rounded-xl px-4 py-3 shadow-xl z-10 hidden sm:block" data-testid="accessibility-wcag-badge">
              <p className="text-cyan-300 text-xs font-bold uppercase tracking-wide">WCAG 2.1</p>
              <p className="text-white text-sm font-semibold">Adjustment Toolkit</p>
            </div>
            <div className="relative rounded-2xl border border-white/10 bg-[#0f1f33] p-10 flex items-center justify-center">
              <div className="absolute inset-8 bg-cyan-500/20 blur-3xl rounded-full" aria-hidden="true" />
              <img src={BOX_IMG} alt="AI Joomla Accessibility Widget" className="relative w-full max-w-xs rounded-xl" data-testid="accessibility-hero-box-image" />
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="border-y border-white/10 bg-[#0a1929]/50 backdrop-blur-md py-6">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map(({ icon: Icon, n, l }) => (
            <div key={l} className="flex items-center gap-3 justify-center text-center md:text-left md:justify-start">
              <Icon aria-hidden="true" className="w-6 h-6 text-cyan-400 flex-shrink-0" strokeWidth={1.5} />
              <div>
                <p className="text-lg font-bold text-white leading-tight">{n}</p>
                <p className="text-slate-400 text-xs">{l}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES BENTO GRID */}
      <section className="py-24 max-w-7xl mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-white mb-4">Every adjustment WCAG 2.1 calls for</h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-base lg:text-lg">All configured from the Joomla plugin manager — nothing to code, nothing to touch in your template.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {CAPABILITIES.map(({ icon: Icon, title, desc, span }) => (
            <div
              key={title}
              className={`group border border-white/10 bg-[#0f1f33] rounded-2xl p-6 hover:border-cyan-400/50 hover:-translate-y-1 transition-transform duration-200 ${span}`}
              data-testid={`accessibility-feature-${title.slice(0, 10).replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-xl bg-cyan-400/10 flex items-center justify-center mb-4 group-hover:bg-cyan-400/20 transition-colors duration-200">
                <Icon aria-hidden="true" className="w-5.5 h-5.5 text-cyan-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">{title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING - box art hero */}
      <section className="border-y border-white/10 bg-[#0a1929]/40 py-24">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-white mb-4">Start free. Upgrade when you\u2019re ready.</h2>
            <p className="text-slate-400 text-base lg:text-lg">Every tier requires a free Registration &amp; Download ID key.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-10 bg-cyan-500/20 blur-3xl rounded-full" aria-hidden="true" />
              <img src={BOX_IMG} alt="AI Joomla Accessibility Widget box art" className="relative w-full max-w-sm rounded-2xl drop-shadow-2xl" data-testid="accessibility-pricing-box-image" />
            </div>
            <div className="space-y-3">
              {(tiers.length ? tiers : [
                { id: 'free', label: 'Free', price: 0, billing: 'free' },
                { id: 'pro', label: 'Pro', price: 49, billing: 'yearly', badge: 'Most Popular' },
                { id: 'pro_lifetime', label: 'Pro Lifetime', price: 149, billing: 'once' },
              ]).map((t) => (
                <div key={t.id} className="flex items-center justify-between border border-white/10 bg-[#0f1f33] rounded-xl px-5 py-4" data-testid={`accessibility-pricing-tier-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{t.label}</span>
                    {t.badge && <span className="text-[10px] font-bold uppercase tracking-wide bg-cyan-400/15 text-cyan-300 px-2 py-0.5 rounded-full">{t.badge}</span>}
                  </div>
                  <span className="text-lg font-bold text-white">
                    {t.price === 0 ? <span className="text-emerald-400">Free</span> : (
                      <>${t.price}<span className="text-xs font-normal text-slate-400">{t.billing === 'once' ? ' one-time' : '/yr'}</span></>
                    )}
                  </span>
                </div>
              ))}
              <Link
                to={BUY_URL}
                className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#22d3ee] hover:bg-[#06b6d4] text-[#050f17] font-bold w-full justify-center transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-[#050f17]"
                data-testid="accessibility-pricing-buy-btn"
              >
                <Sparkles aria-hidden="true" className="w-4 h-4" /> Choose Your License
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl tracking-tight font-semibold text-white text-center mb-10">Frequently asked questions</h2>
          {FAQS.map((faq) => <FaqItem key={faq.q} {...faq} />)}
        </div>
      </section>

      {/* LIVE DEMO — this exact widget is running on aijoomla.org right now */}
      <section id="live-demo" className="py-24 bg-gradient-to-br from-[#0a1929] to-[#050f17] border-t border-white/10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/25 text-cyan-300 text-xs font-bold uppercase tracking-[0.2em] mb-6">
            <Sparkles aria-hidden="true" className="w-3.5 h-3.5" /> Live On This Site
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="accessibility-live-demo-title">
            Try it right now — this is the exact widget
          </h2>
          <p className="text-slate-400 text-lg mb-6 max-w-xl mx-auto">
            We installed our own accessibility widget on aijoomla.org. Click the glowing button in the
            bottom-right corner of your screen to open the panel and adjust text size, contrast, dyslexia
            font, screen reader and more — live, right here.
          </p>
          <div className="flex items-center justify-center gap-2 text-cyan-300 text-sm font-semibold animate-bounce">
            <ArrowRight aria-hidden="true" className="w-4 h-4 rotate-90" /> Look bottom-right
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-32 bg-gradient-to-br from-[#0a1929] to-[#050f17] border-t border-white/10">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <ShieldCheck aria-hidden="true" className="w-12 h-12 text-cyan-400 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="text-4xl font-bold text-white mb-4">Make your site accessible to everyone</h2>
          <p className="text-slate-400 text-lg mb-8">Install in minutes on any Joomla 4, 5 or 6 site — no coding, no template edits.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to={BUY_URL}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-[#22d3ee] hover:bg-[#06b6d4] text-[#050f17] font-bold text-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-[#050f17]"
              data-testid="accessibility-final-cta-buy"
            >
              <Zap aria-hidden="true" className="w-5 h-5" /> View Pricing &amp; Buy
            </Link>
            <Link
              to="/docs/ai-joomla-accessibility-widget"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg border border-white/20 hover:border-white/40 text-white font-medium text-lg transition-colors duration-200"
              data-testid="accessibility-final-cta-docs"
            >
              View Documentation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
